import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Building2,
  AlertCircle,
  LogOut,
  Scale,
  GraduationCap
} from 'lucide-react';

const ROLE_OPTIONS = [
  'Defense Attorney (Private Practice)',
  'Public Defender',
  'Prosecutor',
  'Paralegal',
  'Legal Researcher',
  'Journalist',
  'Academic/Researcher',
  'Law Student',
  'Private Investigator',
  'Policy Advocate',
  'Civil Rights Organization',
  'Insurance Professional',
  'Other'
];

const BAR_STATES = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
  'District of Columbia'
];

const ProfileCompletion = () => {
  const [formData, setFormData] = useState({
    role: '',
    barNumber: '',
    barState: '',
    organization: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { updateUserProfile, logout, setPendingNav } = useAuth();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.role) {
      setError('Please select your role');
      return;
    }

    // Require organization for non-attorneys
    const attorneyRoles = [
      'Defense Attorney (Private Practice)',
      'Public Defender',
      'Prosecutor'
    ];
    if (!attorneyRoles.includes(formData.role) && !formData.organization) {
      setError('Please provide your organization or institution');
      return;
    }

    try {
      setError('');
      setLoading(true);

      console.log('ProfileCompletion: Starting profile update...');

      // Store profile data
      const profileData = {
        role: formData.role,
        barNumber: formData.barNumber || null,
        barState: formData.barState || null,
        organization: formData.organization || null,
        completedAt: new Date().toISOString()
      };

      // Update user profile with additional information
      console.log('ProfileCompletion: Calling updateUserProfile...');
      await updateUserProfile(profileData);
      console.log(
        'ProfileCompletion: updateUserProfile completed successfully'
      );

      console.log('ProfileCompletion: Setting pending navigation to /app...');
      // Use pending navigation to handle race condition with Firestore data loading
      setPendingNav('/app');
    } catch (error) {
      console.error('Profile update error:', error);
      setError('Failed to update profile. Please try again');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img
            src="/briefed_logo_transp.png"
            alt="Briefed Logo"
            className="h-12 w-auto"
          />
        </div>
        <h2 className="mt-6 text-center text-3xl font-light text-gray-900">
          Complete Your Profile
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Please provide your professional information to continue
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="role"
                className="block text-sm font-medium text-gray-700"
              >
                Your Role
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Scale className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  id="role"
                  name="role"
                  required
                  value={formData.role}
                  onChange={handleChange}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select your role</option>
                  {ROLE_OPTIONS.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Bar Number and State - only show for attorneys */}
            {[
              'Defense Attorney (Private Practice)',
              'Public Defender',
              'Prosecutor'
            ].includes(formData.role) && (
              <>
                <div>
                  <label
                    htmlFor="barNumber"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Bar Number (Optional)
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <GraduationCap className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="barNumber"
                      name="barNumber"
                      type="text"
                      value={formData.barNumber}
                      onChange={handleChange}
                      className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Enter your bar number"
                    />
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Helps verify your professional credentials
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="barState"
                    className="block text-sm font-medium text-gray-700"
                  >
                    State/Bar Association
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building2 className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      id="barState"
                      name="barState"
                      value={formData.barState}
                      onChange={handleChange}
                      className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="">Select state</option>
                      {BAR_STATES.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}

            {/* Organization - required for non-attorneys, optional for attorneys */}
            {![
              'Defense Attorney (Private Practice)',
              'Public Defender',
              'Prosecutor'
            ].includes(formData.role) && (
              <div>
                <label
                  htmlFor="organization"
                  className="block text-sm font-medium text-gray-700"
                >
                  Organization/Institution
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building2 className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="organization"
                    name="organization"
                    type="text"
                    autoComplete="organization"
                    required
                    value={formData.organization}
                    onChange={handleChange}
                    className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="University, media outlet, government agency, etc."
                  />
                </div>
              </div>
            )}

            {/* Optional organization for attorneys */}
            {[
              'Defense Attorney (Private Practice)',
              'Public Defender',
              'Prosecutor'
            ].includes(formData.role) && (
              <div>
                <label
                  htmlFor="organization"
                  className="block text-sm font-medium text-gray-700"
                >
                  Law Firm/Organization (Optional)
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building2 className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="organization"
                    name="organization"
                    type="text"
                    autoComplete="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Law firm, government agency, etc."
                  />
                </div>
              </div>
            )}

            <div className="space-y-3">
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating Profile...' : 'Complete Profile'}
              </button>

              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletion;
